import _ from 'lodash';
import { useState } from 'react';
import { type PortalSettings as PortalSettingsType } from '@gorilla/portal-shared/src/schemas';
import { type Portal, REGION_NAMES } from '../services/portal';
import { Heading, Text, Box, Flex, Icon, Divider, Button, Skeleton } from '@vibe/core';
import { Academy, ExternalPage, Activity, MoveArrowRight, Video, Forum, Education, WhatsNew } from '@vibe/icons';
import { checkPortalManagerPermissions } from '@gorilla/portal-shared/src/utils';
import { DashboardStats } from './DashboardStats';
import { DashboardUptimeStats } from './DashboardUptimeStats';
import { OnboardingModal } from './OnboardingModal';
import { type FrontendAnalyticsData } from '../services/analytics';
import { useUptime } from '../hooks/use-uptime';
import { monday } from '../services/monday';
import { ExternalLink } from './ExternalLink';

type DashboardProps = {
  themeSettings: any;
  analyticsData: FrontendAnalyticsData;
  portalSettings: PortalSettingsType;
  isPortalPublished: boolean;
  registryEntries: any;
  portal: Portal;
  region: keyof typeof REGION_NAMES;
  hasUnpublishedChanges: boolean | undefined;
  portalManagerPermissions: string[];
  mondayAccountSlug: string;
  screenMode: 'small' | 'medium' | 'large';
  setView: (view: 'dashboard' | 'pages' | 'theme' | 'settings') => void;
};

export function Dashboard({
  themeSettings,
  region,
  portalManagerPermissions,
  screenMode,
  setView,
  analyticsData,
  isPortalPublished,
  portal,
  registryEntries,
}: DashboardProps) {
  const permissionCheck = checkPortalManagerPermissions(portalManagerPermissions);
  const { data: uptimeData } = useUptime(region);
  const [isOnboardingModalOpen, setIsOnboardingModalOpen] = useState(() => {
    return window.localStorage.getItem(`cpb-${portal.id}-onboarding--dismissed`) !== 'yes';
  });
  const [onboardingStep, setOnboardingStep] = useState<number | null>(null);

  return (
    <div
      style={{
        overflow: 'hidden',
        overflowY: 'auto',
        height: '100%',
        maxHeight: 'calc(100vh - 73px)',
      }}
    >
      {!permissionCheck.hasAllPermissions ? (
        <Box
          style={{
            flex: 1,
            width: '100%',
            backgroundColor: '#ffcb00',
          }}
        >
          <Box padding="large" paddingTop="medium" paddingBottom="medium">
            <Flex direction="row" justify="center" align="center" gap={6}>
              <Text type="text2" weight="normal" align="center" color="primary">
                Your app version lacks permissions, grant them now in the portal settings.
              </Text>
              <Button
                kind="secondary"
                size="xs"
                color="inverted"
                onClick={() => {
                  setView('settings');
                }}
                style={{
                  marginLeft: '10px',
                }}
              >
                Resolve
              </Button>
            </Flex>
          </Box>
        </Box>
      ) : null}

      <Box
        margin="large"
        style={{
          overflow: 'visible',
        }}
      >
        <OnboardingModal
          isOpen={isOnboardingModalOpen}
          onClose={() => {
            setIsOnboardingModalOpen(false);
            window.localStorage.setItem(`cpb-${portal.id}-onboarding--dismissed`, 'yes');
          }}
          initialStep={onboardingStep}
        />

        <Flex
          gap={24}
          direction="column"
          style={{
            width: '100%',
          }}
        >
          <Flex
            gap={24}
            align="stretch"
            style={{
              flexDirection: screenMode === 'small' ? 'column-reverse' : 'row',
              width: 'inherit',
            }}
          >
            <Flex
              direction="column"
              style={{
                width: screenMode === 'small' ? '100%' : '370px',
                minWidth: screenMode === 'small' ? '100%' : '370px',
                maxWidth: screenMode === 'small' ? '100%' : '370px',
              }}
            >
              <Box
                backgroundColor="primaryBackgroundColor"
                borderColor="uiBorderColor"
                border
                rounded="small"
                shadow="xs"
                style={{
                  width: '100%',
                }}
              >
                <Flex
                  align={screenMode === 'small' ? 'stretch' : 'start'}
                  style={{
                    flexDirection: screenMode === 'small' ? 'row-reverse' : 'column',
                    width: '100%',
                  }}
                >
                  <Box
                    style={{
                      width: '100%',
                    }}
                    className="dashboard__teaser--external"
                  >
                    <div
                      onClick={() => {
                        monday.execute('openLinkInTab', { url: 'https://status.getgorilla.app' });
                      }}
                    >
                      <Flex gap={0} direction="column" justify="start" align="start">
                        <Box
                          padding="xl"
                          paddingTop="large"
                          paddingEnd="large"
                          paddingBottom="large"
                          style={{
                            width: 'calc(100% - 56px)',
                          }}
                        >
                          <Flex direction="column" justify="start" align="start" gap={16}>
                            <Flex gap={10} style={{ width: '100%' }}>
                              <Icon icon={Activity} size={24} style={{ transform: 'translate3d(-1px,0,0)' }} />
                              <Heading type="h3" weight="bold" align="start">
                                System uptime
                              </Heading>
                              <div style={{ marginLeft: 'auto' }}>
                                <Icon icon={ExternalPage} size={16} />
                              </div>
                            </Flex>
                            {screenMode !== 'small' ? (
                              uptimeData?.daily ? (
                                <DashboardUptimeStats daily={uptimeData.daily} />
                              ) : (
                                <div style={{ paddingTop: '5px', width: '100%', height: '45px' }}>
                                  <Skeleton width={304} height={36} />
                                </div>
                              )
                            ) : null}
                            <div
                              style={{
                                marginTop: screenMode === 'small' ? '0' : '-10px',
                              }}
                            >
                              <Text type="text2" color="primary" align="start" ellipsis={false}>
                                The average system uptime of published client portals in your region has been at{' '}
                                <strong>
                                  {uptimeData?.six_month_uptime ? `${uptimeData.six_month_uptime.uptime_percentage.toFixed(2)}%` : 'N/A'}
                                </strong>{' '}
                                in the past six months.
                              </Text>
                            </div>
                          </Flex>
                        </Box>
                      </Flex>
                    </div>
                  </Box>
                  <div
                    style={{
                      width: screenMode === 'small' ? '1px' : '100%',
                    }}
                  >
                    <Divider withoutMargin direction={screenMode === 'small' ? 'vertical' : 'horizontal'} />
                  </div>
                  <Box
                    style={{
                      width: '100%',
                    }}
                    className={screenMode === 'small' ? 'dashboard__teaser--external' : 'dashboard__teaser--externalSecondary'}
                  >
                    <div
                      onClick={() => {
                        monday.execute('openLinkInTab', { url: 'https://getgorilla.app/products/client-portal/docs/security' });
                      }}
                    >
                      <Flex gap={0} direction="column" justify="start" align="start">
                        <Box
                          padding="xl"
                          paddingTop="large"
                          paddingEnd="large"
                          paddingBottom="large"
                          style={{
                            width: 'calc(100% - 56px)',
                          }}
                        >
                          <Flex direction="column" justify="start" align="start" gap={16}>
                            <Flex gap={14} style={{ width: '100%' }}>
                              <Box style={{ lineHeight: 0 }}>
                                <img
                                  src={`/regions/${region}.svg`}
                                  width="20"
                                  height="15"
                                  alt="Region"
                                  style={{ width: 'auto', height: screenMode === 'small' ? '18px' : '18px', borderRadius: '2px' }}
                                />
                              </Box>
                              <Heading type="h3" weight="bold" align="start">
                                Hosted in {REGION_NAMES[region] ? REGION_NAMES[region] : REGION_NAMES['us']}
                              </Heading>
                              <div style={{ marginLeft: 'auto' }}>
                                <Icon icon={ExternalPage} size={16} />
                              </div>
                            </Flex>
                            <Text type="text2" color="primary" align="start" ellipsis={false} style={{ marginRight: '16px' }}>
                              Secure and encrypted regional hosting, with your data staying inside your monday.com account and kept stored
                              in {REGION_NAMES[region] ? REGION_NAMES[region] : REGION_NAMES['us']}.
                            </Text>
                          </Flex>
                        </Box>
                      </Flex>
                    </div>
                  </Box>
                </Flex>
              </Box>
            </Flex>
            {analyticsData ? <DashboardStats screenMode={screenMode} analyticsData={analyticsData} /> : null}
          </Flex>

          {screenMode !== 'small' ? (
            <Flex
              gap={24}
              // @ts-ignore
              direction={screenMode === 'small' ? 'column' : 'row'}
              align="stretch"
              style={{
                width: 'inherit',
              }}
            >
              <Flex
                direction="column"
                gap={24}
                style={{
                  width: '370px',
                  minWidth: '370px',
                  maxWidth: '370px',
                }}
              >
                <Box
                  backgroundColor="primaryBackgroundColor"
                  borderColor="uiBorderColor"
                  border
                  rounded="small"
                  shadow="xs"
                  style={{
                    width: '100%',
                  }}
                >
                  <Flex gap={0} direction="column" justify="start" align="start">
                    <Box
                      padding="xl"
                      paddingTop="large"
                      paddingBottom="large"
                      style={{
                        width: 'calc(100% - 64px)',
                      }}
                    >
                      <Flex direction="column" justify="start" align="start" gap={16}>
                        <Flex gap={10} style={{ width: '100%' }}>
                          <Icon icon={Academy} size={24} style={{ transform: 'translate3d(-1px,0,0)' }} />
                          <Heading type="h3" weight="bold" align="start">
                            Getting started
                          </Heading>
                        </Flex>
                        <Flex gap={1} direction="column" justify="start" align="start" style={{ width: '100%' }}>
                          <Flex
                            gap={8}
                            direction="row"
                            justify="start"
                            align="center"
                            style={{
                              width: '100%',
                            }}
                            className="dashboard__link--external"
                          >
                            <div style={{ flex: 1 }}>
                              <Text type="text2" color="primary" align="start" ellipsis={false} style={{ flex: 1 }}>
                                <ExternalLink
                                  href="https://getgorilla.app/products/client-portal/docs/getting-started/app-views/client-settings-view"
                                  target="_blank"
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    textDecoration: 'none',
                                    color: 'inherit',
                                  }}
                                >
                                  Manage client settings and grant access
                                </ExternalLink>
                              </Text>
                            </div>
                            <Icon icon={MoveArrowRight} size={16} />
                          </Flex>
                          <Flex
                            gap={8}
                            direction="row"
                            justify="start"
                            align="center"
                            style={{
                              width: '100%',
                            }}
                            className="dashboard__link--external"
                          >
                            <div style={{ flex: 1 }}>
                              <Text type="text2" color="primary" align="start" ellipsis={false} style={{ flex: 1 }}>
                                <ExternalLink
                                  href="https://getgorilla.app/products/client-portal/docs/getting-started/app-views"
                                  target="_blank"
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    textDecoration: 'none',
                                    color: 'inherit',
                                  }}
                                >
                                  Set up and manage your app views
                                </ExternalLink>
                              </Text>
                            </div>
                            <Icon icon={MoveArrowRight} size={16} />
                          </Flex>
                          <Flex
                            gap={8}
                            direction="row"
                            justify="start"
                            align="center"
                            style={{
                              width: '100%',
                            }}
                            className="dashboard__link--external"
                          >
                            <div style={{ flex: 1 }}>
                              <Text type="text2" color="primary" align="start" ellipsis={false} style={{ flex: 1 }}>
                                <ExternalLink
                                  href="https://getgorilla.app/products/client-portal/docs/features"
                                  target="_blank"
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    textDecoration: 'none',
                                    color: 'inherit',
                                  }}
                                >
                                  Explore all client portal features
                                </ExternalLink>
                              </Text>
                            </div>
                            <Icon icon={MoveArrowRight} size={16} />
                          </Flex>
                        </Flex>
                      </Flex>
                    </Box>
                  </Flex>
                </Box>
              </Flex>

              <Box
                rounded="small"
                shadow="xs"
                style={{
                  flex: 1,
                  width: '100%',
                  backgroundColor: '#3071e2',
                }}
              >
                <div
                  className="dashboard__teaser--internal"
                  onClick={() => {
                    monday.execute('openLinkInTab', { url: 'https://www.youtube.com/watch?v=Pf0J6atpi50' });
                  }}
                >
                  <Box padding="xl" paddingTop="large" paddingBottom="large">
                    <Flex direction="column" justify="start" align="start" gap={16} style={{ width: '100%' }}>
                      <Flex direction="row" justify="start" align="center" gap={16} style={{ width: 'inherit' }}>
                        <Flex gap={10} style={{ width: 'calc(100% - 80px)' }}>
                          <div>
                            <Icon icon={Video} size={24} style={{ transform: 'translate3d(0,1px,0)', color: '#ffffff' }} />
                          </div>
                          <Heading type="h3" weight="bold" align="start" color="onPrimary" withoutTooltip>
                            First steps with Simon
                          </Heading>
                        </Flex>
                      </Flex>
                      <div
                        style={{
                          zIndex: 1,
                          position: 'relative',
                          marginRight: '8px',
                          // @ts-ignore
                          maxWidth: screenMode === 'small' ? '100%' : '280px',
                          width: 'calc(100% - 130px)',
                        }}
                      >
                        <Text type="text2" color="onPrimary" align="start" ellipsis withoutTooltip maxLines={3}>
                          Watch the video to get a quick overview of the Client Portal Builder and learn how to use the app.
                        </Text>
                      </div>
                    </Flex>
                  </Box>
                  <Box style={{ pointerEvents: 'none', position: 'absolute', right: '5%', bottom: 0, lineHeight: 0 }}>
                    <img
                      src="/24c09cb3ea552e.webp"
                      alt="Get started with Simon"
                      style={{ width: 'auto', height: screenMode === 'medium' ? '120px' : '135px' }}
                    />
                  </Box>
                </div>
              </Box>

              <Box
                rounded="small"
                shadow="xs"
                style={{
                  flex: 1,
                  width: '100%',
                  backgroundColor: '#3071e2',
                }}
              >
                <div className="dashboard__teaser--internal">
                  <iframe
                    src="https://getgorilla.app/products/client-portal/news"
                    width="100%"
                    height="100%"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  />
                </div>
              </Box>
            </Flex>
          ) : null}

          <Flex
            gap={screenMode === 'small' ? 0 : 24}
            direction={screenMode === 'small' ? 'column' : 'row'}
            align="stretch"
            style={{
              width: 'inherit',
            }}
          >
            <Flex
              direction="column"
              gap={24}
              style={{
                width: screenMode === 'small' ? '100%' : '370px',
                minWidth: screenMode === 'small' ? '100%' : '370px',
                maxWidth: screenMode === 'small' ? '100%' : '370px',
              }}
            >
              <Box
                backgroundColor="primaryBackgroundColor"
                borderColor="uiBorderColor"
                border
                //rounded="small"
                shadow="xs"
                style={{
                  flex: 1,
                  width: '100%',
                  borderTopRightRadius: '4px',
                  borderTopLeftRadius: '4px',
                  borderBottomRightRadius: screenMode === 'small' ? '0px' : '4px',
                  borderBottomLeftRadius: screenMode === 'small' ? '0px' : '4px',
                }}
                className="dashboard__teaser--external"
              >
                <div
                  onClick={() => {
                    monday.execute('openLinkInTab', { url: 'https://getgorilla.app/products/client-portal/docs/introduction' });
                  }}
                >
                  <Box padding="xl" paddingTop="large" paddingEnd="large" paddingBottom="large">
                    <Flex direction="column" justify="start" align="start" gap={16} style={{ width: '100%' }}>
                      <Flex direction="row" justify="start" align="center" gap={16} style={{ width: 'inherit' }}>
                        <Flex gap={10} style={{ width: 'calc(100% - 40px)' }}>
                          <div>
                            <Icon icon={Education} size={24} style={{ transform: 'translate3d(-1px,2px,0)' }} />
                          </div>
                          <Heading type="h3" weight="bold" align="start">
                            Documentation
                          </Heading>
                        </Flex>
                        <div style={{ marginLeft: 'auto' }}>
                          <Icon icon={ExternalPage} size={16} />
                        </div>
                      </Flex>
                      {screenMode !== 'small' && (
                        <div style={{ marginRight: '8px' }}>
                          <Text type="text2" color="primary" align="start" ellipsis={false}>
                            Explore our documentation to learn more about the features and capabilities of the Client Portal Builder app.
                          </Text>
                        </div>
                      )}
                    </Flex>
                  </Box>
                </div>
              </Box>
            </Flex>

            <Box
              backgroundColor="primaryBackgroundColor"
              borderColor="uiBorderColor"
              border
              //rounded="small"
              shadow="xs"
              style={{
                flex: 1,
                marginTop: screenMode === 'small' ? '-1px' : '0px',
                marginLeft: screenMode === 'small' ? '-1px' : '0px',
                width: '100%',
                borderTopRightRadius: screenMode === 'small' ? '0px' : '4px',
                borderTopLeftRadius: screenMode === 'small' ? '0px' : '4px',
                borderBottomRightRadius: screenMode === 'small' ? '0px' : '4px',
                borderBottomLeftRadius: screenMode === 'small' ? '0px' : '4px',
              }}
              className="dashboard__teaser--external"
            >
              <div
                onClick={() => {
                  monday.execute('openLinkInTab', { url: 'https://cal.com/gorilla-apps/client-portal-builder-demo?overlayCalendar=true' });
                }}
              >
                <Box padding="xl" paddingTop="large" paddingEnd="large" paddingBottom="large">
                  <Flex direction="column" justify="start" align="start" gap={16} style={{ width: '100%' }}>
                    <Flex direction="row" justify="start" align="center" gap={16} style={{ width: 'inherit' }}>
                      <Flex gap={10} style={{ width: 'calc(100% - 40px)' }}>
                        <div>
                          <Icon icon={Forum} size={24} style={{ transform: 'translate3d(-1px,2px,0)' }} />
                        </div>
                        <Heading type="h3" weight="bold" align="start" withoutTooltip>
                          Need help?
                        </Heading>
                      </Flex>
                      <div style={{ marginLeft: 'auto' }}>
                        <Icon icon={ExternalPage} size={16} />
                      </div>
                    </Flex>
                    {screenMode !== 'small' && (
                      <div
                        style={{
                          zIndex: 1,
                          position: 'relative',
                          marginRight: '8px',
                          maxWidth: '306px',
                        }}
                      >
                        <Text type="text2" color="primary" align="start" ellipsis withoutTooltip maxLines={3}>
                          Book a free consultation with our team to get the most out of your Client Portal. Let's talk about your use case
                          and answer questions.
                        </Text>
                      </div>
                    )}
                  </Flex>
                </Box>
                {screenMode === 'large' && (
                  <div
                    style={{
                      zIndex: 0,
                      position: 'absolute',
                      right: '5%',
                      bottom: '-47px',
                      lineHeight: 0,
                      color: '#f6f7fb',
                    }}
                  >
                    <Icon icon={Forum} size={100} />
                  </div>
                )}
              </div>
            </Box>

            <Box
              backgroundColor="primaryBackgroundColor"
              borderColor="uiBorderColor"
              border
              //rounded="small"
              shadow="xs"
              style={{
                flex: 1,
                marginTop: screenMode === 'small' ? '-1px' : '0px',
                marginLeft: screenMode === 'small' ? '-1px' : '0px',
                width: '100%',
                borderTopRightRadius: screenMode === 'small' ? '0px' : '4px',
                borderTopLeftRadius: screenMode === 'small' ? '0px' : '4px',
                borderBottomRightRadius: '4px',
                borderBottomLeftRadius: '4px',
              }}
              className="dashboard__teaser--external"
            >
              <div
                onClick={() => {
                  monday.execute('openLinkInTab', { url: 'https://getgorilla.app/products/client-portal/docs/release-notes' });
                }}
              >
                <Box padding="xl" paddingTop="large" paddingEnd="large" paddingBottom="large">
                  <Flex direction="column" justify="start" align="start" gap={16} style={{ width: '100%' }}>
                    <Flex direction="row" justify="start" align="center" gap={16} style={{ width: 'inherit' }}>
                      <Flex gap={10} style={{ width: 'calc(100% - 40px)' }}>
                        <div>
                          <Icon icon={WhatsNew} size={24} style={{ transform: 'translate3d(-1px,2px,0)' }} />
                        </div>
                        <Heading type="h3" weight="bold" align="start" withoutTooltip>
                          What's new?
                        </Heading>
                      </Flex>
                      <div style={{ marginLeft: 'auto' }}>
                        <Icon icon={ExternalPage} size={16} />
                      </div>
                    </Flex>
                    {screenMode !== 'small' && (
                      <div
                        style={{
                          zIndex: 1,
                          position: 'relative',
                          marginRight: '8px',
                          maxWidth: '306px',
                        }}
                      >
                        <Text type="text2" color="primary" align="start" ellipsis withoutTooltip maxLines={3}>
                          Check out the latest features and updates we've added to the app that help you build better portals for your
                          clients.
                        </Text>
                      </div>
                    )}
                  </Flex>
                </Box>
                {screenMode === 'large' && (
                  <div
                    style={{
                      zIndex: 0,
                      position: 'absolute',
                      right: '5%',
                      bottom: '-35px',
                      lineHeight: 0,
                      color: '#f6f7fb',
                    }}
                  >
                    <Icon icon={WhatsNew} size={100} />
                  </div>
                )}
              </div>
            </Box>
          </Flex>
        </Flex>
      </Box>
    </div>
  );
}
